(() => {
  const STORAGE_KEY = 'sanitaerbedarf-nord-wishlist-v1';
  const ACCOUNT_ADAPTER_NAME = 'SanitaerbedarfNordWishlistAccountAdapter';
  const TOAST_DURATION = 3200;
  const REMOVE_DURATION = window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 0 : 180;
  let toastTimer;
  let accountSaveTimer;
  let accountSyncReady = false;
  const wishlistTargetFeedback = new WeakMap();

  const isGerman = () => document.documentElement.lang.toLowerCase().startsWith('de');
  const text = (german, english) => (isGerman() ? german : english);

  const toBoolean = (value) => value === true || value === 'true';

  const safeProductUrl = (value) => {
    try {
      const parsed = new URL(value || '/', window.location.origin);
      if (parsed.origin !== window.location.origin) return '/collections/all';
      return `${parsed.pathname}${parsed.search}${parsed.hash}`;
    } catch (_error) {
      return '/collections/all';
    }
  };

  const safeImageUrl = (value) => {
    if (!value) return '';
    try {
      const parsed = new URL(value, window.location.origin);
      return ['http:', 'https:'].includes(parsed.protocol) ? parsed.href : '';
    } catch (_error) {
      return '';
    }
  };

  const normalizeItem = (item) => {
    if (!item || !item.id || !item.title) return null;

    const purchaseMode = ['direct', 'inquiry', 'rental'].includes(item.purchaseMode)
      ? item.purchaseMode
      : 'inquiry';

    return {
      id: String(item.id),
      handle: String(item.handle || '').replace(/[^a-zA-Z0-9-]/g, ''),
      variantId: String(item.variantId || ''),
      variantTitle: String(item.variantTitle || ''),
      title: String(item.title || '').trim(),
      url: safeProductUrl(item.url),
      image: safeImageUrl(item.image),
      imageAlt: String(item.imageAlt || item.title || '').trim(),
      price: String(item.price || '').trim(),
      priceCents: Number.parseInt(item.priceCents || '0', 10) || 0,
      comparePrice: String(item.comparePrice || '').trim(),
      comparePriceCents: Number.parseInt(item.comparePriceCents || '0', 10) || 0,
      status: String(item.status || '').trim(),
      purchaseMode,
      available: toBoolean(item.available),
      directAdd: toBoolean(item.directAdd),
      requiresSelection: toBoolean(item.requiresSelection),
      savedAt: Number.parseInt(item.savedAt || Date.now(), 10) || Date.now(),
    };
  };

  const deduplicateItems = (items) => {
    const byProductId = new Map();

    items.forEach((candidate) => {
      const item = normalizeItem(candidate);
      if (!item) return;
      const existing = byProductId.get(item.id);
      if (!existing || item.savedAt >= existing.savedAt) byProductId.set(item.id, item);
    });

    return [...byProductId.values()].sort((a, b) => b.savedAt - a.savedAt);
  };

  const getWishlist = () => {
    try {
      const parsed = JSON.parse(window.localStorage.getItem(STORAGE_KEY) || '[]');
      return Array.isArray(parsed) ? deduplicateItems(parsed) : [];
    } catch (_error) {
      return [];
    }
  };

  const persistWishlist = (items) => {
    const normalizedItems = deduplicateItems(items);
    try {
      window.localStorage.setItem(STORAGE_KEY, JSON.stringify(normalizedItems));
      return normalizedItems;
    } catch (_error) {
      return getWishlist();
    }
  };

  const itemFromButton = (button) => normalizeItem({
    id: button.dataset.productId,
    handle: button.dataset.productHandle,
    variantId: button.dataset.variantId,
    variantTitle: button.dataset.variantTitle,
    title: button.dataset.productTitle,
    url: button.dataset.productUrl,
    image: button.dataset.productImage,
    imageAlt: button.dataset.productImageAlt,
    price: button.dataset.productPrice,
    priceCents: button.dataset.productPriceCents,
    comparePrice: button.dataset.productComparePrice,
    comparePriceCents: button.dataset.productComparePriceCents,
    status: button.dataset.productStatus,
    purchaseMode: button.dataset.productPurchaseMode,
    available: button.dataset.productAvailable,
    directAdd: button.dataset.productDirectAdd,
    requiresSelection: button.dataset.productRequiresSelection,
    savedAt: Date.now(),
  });

  const isInWishlist = (productId, items = getWishlist()) => (
    items.some((item) => item.id === String(productId))
  );

  const setButtonState = (button, active) => {
    const productTitle = button.dataset.productTitle || text('Produkt', 'Product');
    const addLabel = text(
      `${productTitle} zur Merkliste hinzufügen`,
      `Add ${productTitle} to wishlist`,
    );
    const removeLabel = text(
      `${productTitle} von der Merkliste entfernen`,
      `Remove ${productTitle} from wishlist`,
    );

    button.classList.toggle('is-active', active);
    button.setAttribute('aria-pressed', active ? 'true' : 'false');
    button.setAttribute('aria-label', active ? removeLabel : addLabel);
    button.title = active
      ? text('Von der Merkliste entfernen', 'Remove from wishlist')
      : text('Zur Merkliste hinzufügen', 'Add to wishlist');
  };

  const updateWishlistButtons = (root = document, items = getWishlist()) => {
    const productIds = new Set(items.map((item) => item.id));
    root.querySelectorAll('[data-uis24-wishlist-toggle]').forEach((button) => {
      setButtonState(button, productIds.has(String(button.dataset.productId)));
    });
  };

  const updateWishlistCount = (items = getWishlist()) => {
    const count = items.length;
    const visualCount = count > 99 ? '99+' : String(count);
    const accessibleCount = text(
      count === 1 ? '1 Produkt auf der Merkliste' : `${count} Produkte auf der Merkliste`,
      count === 1 ? '1 product on wishlist' : `${count} products on wishlist`,
    );

    document.querySelectorAll('[data-uis24-wishlist-link]').forEach((link) => {
      link.classList.toggle('is-active', count > 0);
      link.setAttribute('aria-label', text(
        count === 0 ? 'Merkliste öffnen' : (count === 1 ? 'Merkliste mit 1 Produkt öffnen' : `Merkliste mit ${count} Produkten öffnen`),
        count === 0 ? 'Open wishlist' : (count === 1 ? 'Open wishlist with 1 product' : `Open wishlist with ${count} products`),
      ));
    });

    document.querySelectorAll('[data-uis24-wishlist-count]').forEach((counter) => {
      counter.hidden = count === 0;
      counter.querySelector('[data-uis24-wishlist-count-value]')?.replaceChildren(visualCount);
      counter.querySelector('[data-uis24-wishlist-count-label]')?.replaceChildren(accessibleCount);
    });
  };

  const announce = (message) => {
    document.querySelectorAll('[data-uis24-wishlist-live]').forEach((region) => {
      region.textContent = message;
    });
  };

  const ensureToast = () => {
    let toast = document.querySelector('[data-uis24-wishlist-toast]');
    if (toast) return toast;

    toast = document.createElement('div');
    toast.className = 'uis24-wishlist-toast';
    toast.dataset.uis24WishlistToast = '';
    toast.setAttribute('role', 'status');
    toast.setAttribute('aria-live', 'polite');

    const message = document.createElement('span');
    message.dataset.uis24WishlistToastMessage = '';
    const link = document.createElement('a');
    link.href = document.querySelector('[data-uis24-wishlist-link]')?.getAttribute('href') || '/pages/merkliste';
    link.textContent = text('Merkliste ansehen', 'View wishlist');

    toast.append(message, link);
    document.body.appendChild(toast);
    return toast;
  };

  const showToast = (message) => {
    const toast = ensureToast();
    toast.querySelector('[data-uis24-wishlist-toast-message]')?.replaceChildren(message);
    toast.classList.add('is-visible');
    window.clearTimeout(toastTimer);
    toastTimer = window.setTimeout(() => toast.classList.remove('is-visible'), TOAST_DURATION);
  };

  const getWishlistTarget = () => (
    [...document.querySelectorAll('[data-uis24-wishlist-link]')].find((link) => {
      const rect = link.getBoundingClientRect();
      return rect.width > 0 && rect.height > 0;
    }) || null
  );

  const confirmWishlistTarget = (target, reducedMotion = false) => {
    if (!target) return;

    const previousFeedback = wishlistTargetFeedback.get(target);
    previousFeedback?.animation?.cancel();
    window.clearTimeout(previousFeedback?.timer);
    target.classList.add('is-receiving');

    const animation = reducedMotion || typeof target.animate !== 'function'
      ? null
      : target.animate(
        [
          { transform: 'scale(1)' },
          { transform: 'scale(1.1)', offset: 0.45 },
          { transform: 'scale(1)' },
        ],
        {
          duration: 220,
          easing: 'cubic-bezier(0.23, 1, 0.32, 1)',
        },
      );

    const timer = window.setTimeout(() => {
      target.classList.remove('is-receiving');
      wishlistTargetFeedback.delete(target);
    }, reducedMotion ? 420 : 260);

    wishlistTargetFeedback.set(target, { animation, timer });
  };

  const animateWishlistTransfer = (sourceButton) => {
    const target = getWishlistTarget();
    if (!target) return;

    const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (reducedMotion) {
      confirmWishlistTarget(target, true);
      return;
    }

    const sourceIcon = sourceButton.querySelector('.icon-heart');
    const targetIcon = target.querySelector('.icon-heart');
    if (!sourceIcon || !targetIcon || typeof sourceIcon.animate !== 'function') {
      confirmWishlistTarget(target);
      return;
    }

    const sourceRect = sourceIcon.getBoundingClientRect();
    const targetRect = targetIcon.getBoundingClientRect();
    const size = 24;
    const startX = sourceRect.left + (sourceRect.width / 2) - (size / 2);
    const startY = sourceRect.top + (sourceRect.height / 2) - (size / 2);
    const endX = targetRect.left + (targetRect.width / 2) - (size / 2);
    const endY = targetRect.top + (targetRect.height / 2) - (size / 2);
    const distanceY = endY - startY;
    const lift = Math.min(70, Math.max(24, Math.abs(distanceY) * 0.14));
    const middleX = startX + ((endX - startX) * 0.48);
    const middleY = startY + (distanceY * 0.42) - lift;

    const flyer = document.createElement('span');
    flyer.className = 'uis24-wishlist-flyer';
    flyer.setAttribute('aria-hidden', 'true');
    flyer.appendChild(sourceIcon.cloneNode(true));
    document.body.appendChild(flyer);

    const animation = flyer.animate(
      [
        {
          transform: `translate3d(${startX}px, ${startY}px, 0) scale(0.88) rotate(0deg)`,
          opacity: 0.55,
        },
        {
          transform: `translate3d(${middleX}px, ${middleY}px, 0) scale(1.08) rotate(-7deg)`,
          opacity: 1,
          offset: 0.42,
        },
        {
          transform: `translate3d(${endX}px, ${endY}px, 0) scale(0.78) rotate(3deg)`,
          opacity: 0.82,
        },
      ],
      {
        duration: 460,
        easing: 'cubic-bezier(0.77, 0, 0.175, 1)',
        fill: 'forwards',
      },
    );

    let completed = false;
    let cleanupTimer;
    const complete = () => {
      if (completed) return;
      completed = true;
      window.clearTimeout(cleanupTimer);
      flyer.remove();
      confirmWishlistTarget(target);
    };

    animation.addEventListener('finish', complete, { once: true });
    animation.addEventListener('cancel', complete, { once: true });
    cleanupTimer = window.setTimeout(complete, 560);
  };

  const getAccountContext = () => document.querySelector('[data-uis24-wishlist-context]');
  const isCustomerAuthenticated = () => getAccountContext()?.dataset.customerAuthenticated === 'true';
  const getAccountAdapter = () => window[ACCOUNT_ADAPTER_NAME];

  const setAccountStatus = (state) => {
    const status = document.querySelector('[data-uis24-account-sync-status]');
    if (!status) return;

    if (state === 'synced') {
      status.textContent = text(
        'Ihre Wunschliste ist mit Ihrem Kundenkonto synchronisiert.',
        'Your wishlist is synced with your customer account.',
      );
    } else if (state === 'error') {
      status.textContent = text(
        'Die Kontosynchronisierung ist momentan nicht erreichbar. Ihre Auswahl bleibt sicher in diesem Browser gespeichert.',
        'Account sync is currently unavailable. Your selection remains saved in this browser.',
      );
    } else {
      status.textContent = text(
        'Ihre Merkliste bleibt aktuell in diesem Browser gespeichert. Die geräteübergreifende Kontospeicherung wird noch eingerichtet.',
        'Your wishlist is currently saved in this browser. Cross-device account storage still needs to be configured.',
      );
    }
  };

  const queueAccountSave = (items) => {
    if (!accountSyncReady || !isCustomerAuthenticated()) return;
    const adapter = getAccountAdapter();
    if (!adapter || typeof adapter.save !== 'function') return;

    window.clearTimeout(accountSaveTimer);
    accountSaveTimer = window.setTimeout(async () => {
      try {
        await adapter.save(items);
        setAccountStatus('synced');
      } catch (_error) {
        setAccountStatus('error');
      }
    }, 250);
  };

  const dispatchWishlistUpdated = (items) => {
    const detail = { items, count: items.length };
    document.dispatchEvent(new CustomEvent('wishlist:updated', { detail }));
    document.dispatchEvent(new CustomEvent('uis24:wishlist:change', { detail }));
  };

  const saveWishlist = (items, options = {}) => {
    const normalizedItems = persistWishlist(items);
    updateWishlistButtons(document, normalizedItems);
    updateWishlistCount(normalizedItems);
    renderWishlistPage(normalizedItems);
    dispatchWishlistUpdated(normalizedItems);
    if (options.syncAccount !== false) queueAccountSave(normalizedItems);
    return normalizedItems;
  };

  const addToWishlist = (item, options = {}) => {
    const normalizedItem = normalizeItem(item);
    if (!normalizedItem) return getWishlist();

    const existingItems = getWishlist().filter((savedItem) => savedItem.id !== normalizedItem.id);
    const items = saveWishlist([normalizedItem, ...existingItems]);
    if (options.silent !== true) {
      const message = text(
        'Produkt gespeichert. Ihre Merkliste finden Sie oben beim Herz.',
        'Product saved. Find your wishlist at the heart in the header.',
      );
      showToast(message);
      announce(message);
    }
    return items;
  };

  const removeFromWishlist = (productId, options = {}) => {
    const items = saveWishlist(getWishlist().filter((item) => item.id !== String(productId)));
    if (options.silent !== true) {
      const message = text('Produkt von der Merkliste entfernt.', 'Product removed from wishlist.');
      showToast(message);
      announce(message);
    }
    return items;
  };

  const toggleWishlistItem = (item) => {
    if (!item) return false;
    if (isInWishlist(item.id)) {
      removeFromWishlist(item.id);
      return false;
    }
    addToWishlist(item);
    return true;
  };

  const iconMarkup = {
    remove: '<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M4 7h16M9 7V4h6v3m3 0-1 13H7L6 7m4 4v5m4-5v5"/></svg>',
    cart: '<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M3 4h2l2.2 10.2a2 2 0 0 0 2 1.6h7.9a2 2 0 0 0 2-1.6L20.5 8H6.1M10 20a1 1 0 1 1-2 0 1 1 0 0 1 2 0Zm9 0a1 1 0 1 1-2 0 1 1 0 0 1 2 0Z"/><path d="M15.5 3v5M13 5.5h5"/></svg>',
    arrow: '<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M5 12h14m-5-5 5 5-5 5"/></svg>',
  };

  const createRemoveButton = (item) => {
    const button = document.createElement('button');
    button.className = 'uis24-wishlist-item__remove';
    button.type = 'button';
    button.dataset.uis24WishlistRemove = item.id;
    button.innerHTML = iconMarkup.remove;
    button.setAttribute('aria-label', text(
      `${item.title} aus der Wunschliste entfernen`,
      `Remove ${item.title} from wishlist`,
    ));
    button.title = text('Entfernen', 'Remove');
    return button;
  };

  const createImageAction = (item) => {
    const wrapper = document.createElement('div');
    wrapper.className = 'uis24-wishlist-item__media-action';

    if (item.purchaseMode === 'inquiry' || item.purchaseMode === 'rental') {
      const link = document.createElement('a');
      const query = `produkt=${encodeURIComponent(item.title)}${item.handle ? `&handle=${encodeURIComponent(item.handle)}` : ''}`;
      link.href = item.purchaseMode === 'rental'
        ? `/pages/kontakt?anfrage=miete&${query}#kontakt`
        : `/pages/produktanfrage?${query}`;
      link.className = 'uis24-wishlist-item__quick-action uis24-wishlist-item__quick-action--secondary';
      link.innerHTML = iconMarkup.arrow;
      link.setAttribute('aria-label', text(`Artikel anfragen: ${item.title}`, `Request item: ${item.title}`));
      link.title = text('Artikel anfragen', 'Request item');
      wrapper.appendChild(link);
      return wrapper;
    }

    if (!item.available) {
      const button = document.createElement('button');
      button.type = 'button';
      button.className = 'uis24-wishlist-item__quick-action';
      button.innerHTML = iconMarkup.cart;
      button.disabled = true;
      button.setAttribute('aria-label', text(`Nicht verfügbar: ${item.title}`, `Unavailable: ${item.title}`));
      button.title = text('Nicht verfügbar', 'Unavailable');
      wrapper.appendChild(button);
      return wrapper;
    }

    if (!item.directAdd || item.requiresSelection || !item.variantId) {
      const link = document.createElement('a');
      link.href = item.url;
      link.className = 'uis24-wishlist-item__quick-action';
      link.innerHTML = iconMarkup.cart;
      link.setAttribute('aria-label', text(`Optionen wählen: ${item.title}`, `Choose options: ${item.title}`));
      link.title = text('Optionen wählen', 'Choose options');
      wrapper.appendChild(link);
      return wrapper;
    }

    const form = document.createElement('form');
    form.method = 'post';
    form.action = window.routes?.cart_add_url || '/cart/add';
    form.enctype = 'multipart/form-data';
    form.dataset.uis24WishlistAdd = '';
    form.dataset.productTitle = item.title;
    form.dataset.productPriceCents = String(item.priceCents);

    const input = document.createElement('input');
    input.type = 'hidden';
    input.name = 'id';
    input.value = item.variantId;

    const button = document.createElement('button');
    button.type = 'submit';
    button.name = 'add';
    button.className = 'uis24-wishlist-item__quick-action';
    button.innerHTML = iconMarkup.cart;
    button.setAttribute('aria-label', text(`In den Warenkorb: ${item.title}`, `Add to cart: ${item.title}`));
    button.title = text('In den Warenkorb', 'Add to cart');

    form.append(input, button);
    wrapper.appendChild(form);
    return wrapper;
  };

  const createWishlistItem = (item) => {
    const listItem = document.createElement('li');
    listItem.dataset.productId = item.id;

    const article = document.createElement('article');
    article.className = 'uis24-wishlist-item';

    const media = document.createElement('div');
    media.className = 'uis24-wishlist-item__media';
    const imageLink = document.createElement('a');
    imageLink.className = 'uis24-wishlist-item__image';
    imageLink.href = item.url;
    imageLink.setAttribute('aria-label', item.title);

    if (item.image) {
      const image = document.createElement('img');
      image.src = item.image;
      image.alt = item.imageAlt || item.title;
      image.loading = 'lazy';
      image.decoding = 'async';
      image.width = 700;
      image.height = 700;
      image.addEventListener('error', () => image.remove(), { once: true });
      imageLink.appendChild(image);
    }

    media.append(imageLink, createRemoveButton(item), createImageAction(item));

    const body = document.createElement('div');
    body.className = 'uis24-wishlist-item__body';
    const title = document.createElement('h2');
    title.className = 'uis24-wishlist-item__title';
    const titleLink = document.createElement('a');
    titleLink.href = item.url;
    titleLink.textContent = item.title;
    title.appendChild(titleLink);
    body.appendChild(title);

    if (item.status) {
      const status = document.createElement('p');
      status.className = 'uis24-wishlist-item__status';
      status.textContent = item.status;
      body.appendChild(status);
    }

    const price = document.createElement('p');
    price.className = 'uis24-wishlist-item__price';
    const currentPrice = document.createElement('span');
    currentPrice.textContent = item.price;
    price.appendChild(currentPrice);
    if (item.comparePrice && item.comparePriceCents > item.priceCents) {
      const comparePrice = document.createElement('s');
      comparePrice.textContent = item.comparePrice;
      price.appendChild(comparePrice);
    }
    body.appendChild(price);

    article.append(media, body);
    listItem.appendChild(article);
    return listItem;
  };

  function renderWishlistPage(items = getWishlist()) {
    const root = document.querySelector('[data-uis24-wishlist-page]');
    if (!root) return;

    const emptyState = root.querySelector('[data-uis24-wishlist-empty]');
    const grid = root.querySelector('[data-uis24-wishlist-grid]');
    if (!emptyState || !grid) return;

    const fragment = document.createDocumentFragment();
    items.forEach((item) => fragment.appendChild(createWishlistItem(item)));
    grid.replaceChildren(fragment);
    emptyState.hidden = items.length > 0;
    grid.hidden = items.length === 0;
  }

  const productHandleFromItem = (item) => {
    if (item.handle) return item.handle;
    const match = item.url.match(/\/products\/([^/?#]+)/);
    return match ? match[1].replace(/[^a-zA-Z0-9-]/g, '') : '';
  };

  const validateWishlistItem = async (item) => {
    const handle = productHandleFromItem(item);
    if (!handle) return item;

    try {
      const response = await fetch(`/products/${encodeURIComponent(handle)}.js`, {
        headers: { Accept: 'application/json' },
      });
      if (response.status === 404) return null;
      if (!response.ok) return item;

      const product = await response.json();
      const savedVariant = product.variants?.find((variant) => String(variant.id) === item.variantId);
      return normalizeItem({
        ...item,
        handle,
        title: product.title || item.title,
        available: savedVariant ? savedVariant.available : Boolean(product.available),
        directAdd: item.directAdd && Boolean(savedVariant),
        requiresSelection: item.requiresSelection || (!savedVariant && (product.variants?.length || 0) > 1),
        image: item.image || product.featured_image,
      });
    } catch (_error) {
      return item;
    }
  };

  const validateWishlist = async () => {
    if (!document.querySelector('[data-uis24-wishlist-page]')) return;
    const currentItems = getWishlist();
    const validatedItems = (await Promise.all(currentItems.map(validateWishlistItem))).filter(Boolean);
    if (JSON.stringify(validatedItems) !== JSON.stringify(currentItems)) {
      saveWishlist(validatedItems, { syncAccount: false });
    }
  };

  const syncWishlistWithCustomer = async () => {
    if (!isCustomerAuthenticated()) return getWishlist();
    const adapter = getAccountAdapter();
    if (!adapter || typeof adapter.load !== 'function' || typeof adapter.save !== 'function') {
      accountSyncReady = false;
      setAccountStatus('local');
      return getWishlist();
    }

    try {
      const remoteItems = await adapter.load();
      const mergedItems = deduplicateItems([...getWishlist(), ...(Array.isArray(remoteItems) ? remoteItems : [])]);
      persistWishlist(mergedItems);
      await adapter.save(mergedItems);
      accountSyncReady = true;
      setAccountStatus('synced');
      updateWishlistButtons(document, mergedItems);
      updateWishlistCount(mergedItems);
      renderWishlistPage(mergedItems);
      dispatchWishlistUpdated(mergedItems);
      return mergedItems;
    } catch (_error) {
      accountSyncReady = false;
      setAccountStatus('error');
      return getWishlist();
    }
  };

  const submitWishlistAdd = async (form) => {
    const button = form.querySelector('[type="submit"]');
    if (!button || button.getAttribute('aria-disabled') === 'true') return;

    const cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
    const formData = new FormData(form);
    if (cart?.getSectionsToRender) {
      formData.append('sections', cart.getSectionsToRender().map((section) => section.id));
      formData.append('sections_url', window.location.pathname);
      cart.setActiveElement?.(button);
    }

    const config = typeof fetchConfig === 'function'
      ? fetchConfig('javascript')
      : { method: 'POST', headers: { Accept: 'application/json' } };
    config.headers['X-Requested-With'] = 'XMLHttpRequest';
    delete config.headers['Content-Type'];
    config.body = formData;

    button.setAttribute('aria-disabled', 'true');
    button.classList.add('is-loading');

    try {
      const response = await fetch(window.routes?.cart_add_url || '/cart/add.js', config);
      const result = await response.json();
      if (!response.ok || result.status) throw new Error(result.description || result.message || 'Cart error');

      cart?.renderContents?.(result);
      if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined' && PUB_SUB_EVENTS.cartUpdate) {
        publish(PUB_SUB_EVENTS.cartUpdate, {
          source: 'wishlist',
          productVariantId: String(formData.get('id')),
          cartData: result,
        });
      }
      document.dispatchEvent(new CustomEvent('uis24:add_to_cart', {
        detail: {
          item: result,
          variantId: String(formData.get('id')),
          quantity: 1,
          fallbackTitle: form.dataset.productTitle,
          fallbackPrice: Number.parseInt(form.dataset.productPriceCents || '0', 10),
        },
      }));
      button.classList.add('is-success');
      showToast(text('Produkt wurde in den Warenkorb gelegt.', 'Product added to cart.'));
    } catch (_error) {
      showToast(text(
        'Das Produkt konnte nicht hinzugefügt werden. Bitte öffnen Sie die Produktseite.',
        'The product could not be added. Please open the product page.',
      ));
    } finally {
      button.classList.remove('is-loading');
      window.setTimeout(() => {
        button.classList.remove('is-success');
        button.removeAttribute('aria-disabled');
      }, 1400);
    }
  };

  document.addEventListener('click', (event) => {
    const toggleButton = event.target.closest('[data-uis24-wishlist-toggle]');
    if (toggleButton) {
      event.preventDefault();
      const wasAdded = toggleWishlistItem(itemFromButton(toggleButton));
      if (wasAdded) animateWishlistTransfer(toggleButton);
      return;
    }

    const removeButton = event.target.closest('[data-uis24-wishlist-remove]');
    if (!removeButton) return;
    const listItem = removeButton.closest('li');
    listItem?.classList.add('is-removing');
    window.setTimeout(() => removeFromWishlist(removeButton.dataset.uis24WishlistRemove), REMOVE_DURATION);
  });

  document.addEventListener('submit', (event) => {
    const form = event.target.closest('[data-uis24-wishlist-add]');
    if (!form) return;
    event.preventDefault();
    submitWishlistAdd(form);
  });

  document.addEventListener('shopify:section:load', (event) => {
    const items = getWishlist();
    updateWishlistButtons(event.target, items);
    updateWishlistCount(items);
    renderWishlistPage(items);
  });

  document.addEventListener('uis24:wishlist-adapter-ready', syncWishlistWithCustomer);
  window.addEventListener('storage', (event) => {
    if (event.key !== STORAGE_KEY) return;
    const items = getWishlist();
    updateWishlistButtons(document, items);
    updateWishlistCount(items);
    renderWishlistPage(items);
    dispatchWishlistUpdated(items);
  });

  window.UIS24Wishlist = {
    getWishlist,
    getItems: getWishlist,
    saveWishlist,
    addToWishlist,
    removeFromWishlist,
    isInWishlist,
    updateWishlistCount,
    updateWishlistButtons,
    renderWishlistPage,
    mergeGuestAndCustomerWishlist: syncWishlistWithCustomer,
    syncWishlistWithCustomer,
    toggle: toggleWishlistItem,
    remove: removeFromWishlist,
  };

  const initialize = async () => {
    const items = getWishlist();
    updateWishlistButtons(document, items);
    updateWishlistCount(items);
    renderWishlistPage(items);
    await syncWishlistWithCustomer();
    await validateWishlist();
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initialize, { once: true });
  } else {
    initialize();
  }
})();
